import React, { useEffect, useRef, useState } from 'react';
import type { BulkReviewsProps, LoggedInUser } from './types';
import type { SupplierReview } from './types';

import scss from './BulkReviews.module.scss'

import TopInfoSection from './TopInfoSection'
import ReviewList from './ReviewList'
import YourDetails from './SideBar/YourDetails';
import ReviewAndWin from './SideBar/ReviewAndWin';

import { getDirectoryCardsWithIcon } from '@api/getDirectoryCards'
import type { DirectoryCardInfo, DirectoryCategoryInfo } from '@components/DirectoryCard/types'
import BulkReviewsContext from './context'
import NotificationProvider from "@components/Notification";
import { Loading } from 'ui'
import { deepCopy, sleep } from '@helper/objectHelper';
import { getUserProfile } from '@api/getuserProfile';
import { INITIAL_CATEGORY_COUNT, LOCAL_STORAGE_REVIEWS_KEY } from './constants';

const BulkReviews = ({ currentSupplierId, isUserLoggedIn, loggedInUser, loginUrl, registerUrl, facebookAppId, locationSearchUrl } : BulkReviewsProps) => {
    const [categoryInfo, setCategoryInfo] = useState<DirectoryCardInfo>({ competitionEnabled: false, categories: [] });
    const [reviews, setReviews] = useState<SupplierReview[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [currentUser, setCurrentUser] = useState<LoggedInUser | undefined>(loggedInUser);
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(isUserLoggedIn);
    const [showValidationMessages, setShowValidationMessages] = useState<boolean>(false);
    const [showCount, setShowCount] = useState<number>(INITIAL_CATEGORY_COUNT);

    const detailsSectionRef = useRef<HTMLDivElement>(null);
    const [buttonVisible, setButtonVisible] = useState<boolean>(true);

    useEffect(() => {
        getDirectoryCardsWithIcon(currentSupplierId)
            .then(result => {
                if (typeof result !== 'object' || result == null || !result.hasOwnProperty('competitionEnabled')) {
                    return;
                }

                if (!!result.categories) {
                    initReviewsForBookedSuppliers(result.categories, currentSupplierId);

                    const uniqueCategories: DirectoryCategoryInfo[] = [];
                    const categoryIdSet = new Set();

                    result.categories.forEach(obj => {
                        if (!categoryIdSet.has(obj.categoryId)) {
                            categoryIdSet.add(obj.categoryId);
                            uniqueCategories.push(obj);
                        }
                    });

                    const info: DirectoryCardInfo = {
                        competitionEnabled: result.competitionEnabled,
                        categories: uniqueCategories
                    }

                    setCategoryInfo(info);
                }
                setIsLoading(false);
            });
    }, [isLoggedIn, currentSupplierId]);

    useEffect(() => {
        const options = {
            root: null,
            rootMargin: '0px',
            threshold: 0,
        };

        const detailsObserver = new IntersectionObserver(([entry]) => {
            setButtonVisible(!entry.isIntersecting);
        }, options);

        const setObserver = async () => {
            let observerSet = false;

            while (!observerSet) {
                await sleep(1000);
                if (!!detailsSectionRef.current) {
                    detailsObserver.observe(detailsSectionRef.current);
                    observerSet = true;
                }
            }
        }

        setObserver();
        
        return () => {
            if (detailsSectionRef.current) {
                detailsObserver.unobserve(detailsSectionRef.current);
            }
        };
    }, []);

    const saveUserReviews = (userReviews: SupplierReview[]) => {
        setReviews(userReviews);
        const completeReviews = userReviews.filter(r => r.complete);
        if (!!completeReviews && completeReviews.length > 0) {
            localStorage.setItem(LOCAL_STORAGE_REVIEWS_KEY, JSON.stringify(completeReviews));
        }
    };

    const handleYourDetails = () => {
        if (detailsSectionRef.current) {
          detailsSectionRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const initReviewsForBookedSuppliers = (categories: DirectoryCategoryInfo[], currentSupplierId: number | undefined) => {
        // Check if localStorage has data for LOCAL_STORAGE_REVIEWS_KEY
        const storedReviews = localStorage.getItem(LOCAL_STORAGE_REVIEWS_KEY);
        let counter = 1;

        if (storedReviews) {
            const parsedReviews = JSON.parse(storedReviews) as SupplierReview[];

            if (currentSupplierId && parsedReviews.filter(review => review.supplierId === currentSupplierId).length === 0) {
                categories.forEach(cat => {
                    if (cat.bookedSupplierId === currentSupplierId && !!cat.categoryId) {
                        parsedReviews.push(createReview(counter++,
                            cat.bookedSupplierId,
                            !!cat.bookedSupplierName ? cat.bookedSupplierName : '',
                            cat.categoryId,
                            false,
                            true));
                    }
                });
            }

            setReviews(currentSupplierId ?
                parsedReviews.filter(review => review.supplierId === currentSupplierId) :
                parsedReviews);
            return;
        }

        const userReviews: SupplierReview[] = [];
        categories.forEach(cat => {
            if (!!cat.bookedSupplierId && !!cat.categoryId) {
                userReviews.push(createReview(counter++,
                    cat.bookedSupplierId,
                    !!cat.bookedSupplierName ? cat.bookedSupplierName : '',
                    cat.categoryId,
                    false,
                    true));
            }
        });

        saveUserReviews(userReviews);
    }

    const addEmptyReview = (supplierId: number, supplierName: string, category: DirectoryCategoryInfo, custom: boolean) => {
        if (!!category.categoryId) {
            const addedReview = createReview(-1, supplierId, supplierName, category.categoryId, custom, false);
            addUpdateReview(addedReview);
        }
    }

    const createReview = (id: number, supplierId: number, supplierName: string, supplierCategoryId: number, custom: boolean, prepopulated: boolean): SupplierReview => {
        const createdReview: SupplierReview = {
            id,
            supplierId,
            supplierName,
            supplierCategoryId,
            userStarRating: 0,
            userReview: '',
            expanded: true,
            complete: false,
            custom,
            prepopulated
        };

        return createdReview;
    }

    const addUpdateReview = (review: SupplierReview) => {
        const reviewToAdd = deepCopy(review);
        const updatedReviews = (reviewToAdd.id === -1 ? deepCopy(reviews) : reviews.filter(r => r.id !== reviewToAdd.id));

        if (reviewToAdd.id === -1) {
            const maxId: number | undefined = updatedReviews.reduce((max, obj) => {
                return obj.id > max ? obj.id : max;
            }, 0);
            reviewToAdd.id = (!!maxId ? maxId + 1 : 1);
        }

        updatedReviews.push(reviewToAdd);
        saveUserReviews(updatedReviews);
    };

    const deleteReview = (reviewId: number) => {
        const updatedReviews = reviews.filter(r => r.id !== reviewId);
        saveUserReviews(updatedReviews);
    };

    const getLoggedInUser = async () => {
        setIsLoading(true);
        getUserProfile()
            .then(result => {
                if (!!result) {
                    const user: LoggedInUser = {
                        FirstName: result.FirstName,
                        LastName: result.LastName,
                        Email: result.Email,
                        ProfileImage: result.ProfileImage,
                        WeddingDate: result.WeddingDate ? new Date(result.WeddingDate) : undefined,
                        City: result.City,
                        UserId: result.UserId,
                        Postcode: result.Postcode
                    };
                    setCurrentUser(user);
                    setIsLoggedIn(true);
                }
            }).catch(error => {
                console.warn(error);
                setIsLoading(false);
            });
    }

    const updateUser = (profileUrl: string, imageId: number, firstName?: string, lastName?: string, email?: string, location?: string) => {
        const user = !!currentUser ? deepCopy(currentUser) : {
            FirstName: '',
            LastName: '',
            Email: ''
        };
        user.ProfileImage = profileUrl;
        user.ImageId = imageId;
        user.FirstName = (!!firstName ? firstName : '');
        user.LastName = (!!lastName ? lastName : '');
        user.Email = (!!email ? email : '');
        
        if (!!location) {
            const lastCommaIndex = location.lastIndexOf(',');

            if (lastCommaIndex !== -1) {
                user.Postcode = location.slice(lastCommaIndex + 1).trim();
                user.City = location.slice(0, lastCommaIndex).trim();
            }
        }

        setCurrentUser(user);
    }

    return (
        <NotificationProvider bg={"danger"}>
            <BulkReviewsContext.Provider value={{
                categoryInfo,
                reviews,
                isLoading,
                showValidationMessages,
                isLoggedIn: isLoggedIn,
                loggedInUser: currentUser,
                showCount,
                setShowCount,
                addUpdateReview,
                addEmptyReview,
                deleteReview,
                getLoggedInUser,
                updateUser,
                setShowValidationMessages
            }}>
                <div className="container-xxl">
                    <div className={scss.pageTitle}>
                        <h1>Submit your review</h1>
                        <TopInfoSection showVideoSection={false} />
                    </div>
                </div>
                <div className={scss.pageWrapper}>
                    <div className="container-xxl">
                        <div className={scss.layout}>
                            
                            {isLoading ? <div className={'mb-3'}><Loading /></div> : <>
                                <div className={scss.desktopSticky}>
                                    {categoryInfo?.competitionEnabled && <ReviewAndWin />}
                                </div>
                                <div>
                                    <div className={scss.main} data-testid="reviewContainer">
                                        <ReviewList
                                            key={reviews.length}
                                            currentSupplierId={currentSupplierId}
                                        />
                                    </div>
                                    <div ref={detailsSectionRef}>
                                        <hr/>
                                        <YourDetails
                                            locationSearchUrl={locationSearchUrl}
                                            currentSupplierId={currentSupplierId}
                                        />
                                    </div>
                                </div>  
                                {buttonVisible && <button className={scss.addYourDetailsButton} onClick={handleYourDetails}>Add Your Details</button>}                      
                            </>}
                        </div>
                    </div>
                </div>
            </BulkReviewsContext.Provider>
        </NotificationProvider>
    )
}

export default BulkReviews